import { useState } from 'react';
import { useFormik } from 'formik';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { showSnackbarResponseError } from '@/helpers/helpers';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { MyVaultCatalogType, VaultAccountType } from '@/types/myVaultTypes';
import { AddVaultAccountRailsPayloadType } from '@/api/v1/organization/my-vault/addVaultAccountRailsApi';
import { addVaultAccountRails, loadIncrementalFields } from '../../api/api';
import { fieldsSchema } from '../CreateVaultAccountModal/const/const';
import { RailFields } from '../RailFields/RailFields';
import { OptionCard, OptionGrid } from '../../styles';
import * as Yup from 'yup';
import { Dialog } from '@/components-new/Dialog/Dialog';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';

type AddRailsModalType = {
  isOpen: boolean;
  catalog: MyVaultCatalogType;
  account: VaultAccountType;
  initialRail?: string;
  onClose: () => void;
  onSuccess: () => void;
};

export const AddRailsModal = ({ isOpen, catalog, account, initialRail, onClose, onSuccess }: AddRailsModalType) => {
  const meta = catalog.regions[account.region];
  const [rails, setRails] = useState<string[]>(initialRail ? [initialRail] : []);

  const { data: fieldsResponse } = useQuery({
    queryKey: ['my_vault_incremental_fields', account.id, rails],
    queryFn: () => loadIncrementalFields(account.id, rails),
    enabled: rails.length > 0,
  });
  const fields = fieldsResponse?.body?.fields ?? [];

  const { mutate, data, isPending } = useMutation({
    mutationKey: ['add_vault_account_rails'],
    mutationFn: (payload: AddVaultAccountRailsPayloadType) => addVaultAccountRails(account.id, payload),
    onSuccess(response) {
      if (response?.error) return showSnackbarResponseError(response.error);

      amplitudeService.logEvent(AMPLITUDE_EVENTS.AddVaultAccountRailsSuccess);
      onSuccess();
      onClose();
    },
  });

  const formik = useFormik<{ fields: Record<string, string> }>({
    enableReinitialize: true,
    initialValues: { fields: Object.fromEntries(fields.map((field) => [field.key, ''])) },
    validationSchema: () => (fields.length ? Yup.object({ fields: fieldsSchema(fields) }) : Yup.object({})),
    onSubmit: (values) => mutate({ rails, fields: values.fields }),
  });

  const toggle = (code: string, stored: boolean) => {
    if (stored) return;
    setRails((current) => (current.includes(code) ? current.filter((r) => r !== code) : [...current, code]));
  };

  return (
    <Dialog isOpen={isOpen} header='Add payment instructions' width='760px' onClose={onClose}>
      <Typography fontWeight='semibold' marginBottom='8px'>
        Which transfer types do you want to add?
      </Typography>
      <OptionGrid>
        {meta.rails.map((rail) => {
          const stored = account.rails.includes(rail.code);

          return (
            <OptionCard
              key={rail.code}
              type='button'
              $locked={stored}
              $active={rails.includes(rail.code)}
              onClick={() => toggle(rail.code, stored)}
            >
              <Typography fontWeight='semibold'>
                {rail.label}
                {stored ? ' (already stored)' : ''}
              </Typography>
              <Typography variant='bodySmall'>{rail.hint}</Typography>
            </OptionCard>
          );
        })}
      </OptionGrid>

      {rails.length > 0 && (
        <Box marginTop='16px'>
          <FormikForm value={formik}>
            <Typography fontWeight='semibold' marginBottom='8px'>
              Only the new fields
            </Typography>
            {fields.length === 0 && (
              <Typography variant='bodySmall'>
                Everything needed is already stored. Save to add these transfer types.
              </Typography>
            )}
            <RailFields
              fields={fields}
              holderType={account.holder_type}
              bankName={account.bank_name}
              apiError={data?.error}
            />
          </FormikForm>
        </Box>
      )}

      <Box display='flex' justifyContent='flex-end' columnGap='12px' marginTop='16px'>
        <ButtonNew disabled={rails.length === 0} isLoading={isPending} onClick={() => formik.handleSubmit()}>
          Save and update sharing
        </ButtonNew>
      </Box>
    </Dialog>
  );
};
